'use client';

import Button from '../../../atoms/Button/Button';
import Input from '../../../atoms/Input/Input';
import Separator from '../../../atoms/Separator/Separator';
import Box from '../../../atoms/Box/Box';
import FilterPills from '../FilterPills/FilterPills';
import type {
  ToolbarAction,
  ToolbarExportConfig,
  ToolbarSearchConfig,
  ToolbarFilterConfig,
  ToolbarManageColumnsConfig,
} from '../types';
import styles from './Toolbar.module.scss';

type ComponentSize = 'small' | 'medium' | 'large';

export type ToolbarProps = {
  /** Theme variant */
  theme: 'light' | 'dark';
  /** Component size */
  size?: ComponentSize;
  /** Export button configuration. Omit to hide export button. */
  export?: ToolbarExportConfig;
  /** Search input configuration. Omit to hide search. */
  search?: ToolbarSearchConfig;
  /** Filter button configuration. Omit to hide filter button. */
  filter?: ToolbarFilterConfig;
  /** Manage columns button configuration. Omit to hide manage columns button. */
  manageColumns?: ToolbarManageColumnsConfig;
  /** Custom action buttons */
  actions?: ToolbarAction[];
  /** Custom content to render in the toolbar */
  content?: React.ReactNode;
  /** Custom content to render below the main toolbar row */
  subContent?: React.ReactNode;
  /** i18n labels for localization */
  labels?: {
    /** Label for "Manage Columns" button */
    manageColumns?: string;
    /** Label for "Filter" button */
    filter?: string;
    /** Label for "Filter (X)" button - use {{count}} placeholder */
    filterWithCount?: string;
    /** Placeholder for search input */
    searchPlaceholder?: string;
    /** Label for "Clear All" button in filter pills */
    clearAll?: string;
  };
};

/**
 * Toolbar component for Table actions including search, filter, export, and custom actions.
 *
 * @component
 * @param {ToolbarProps} props - Component props
 * @returns {React.ReactElement} The Toolbar component
 * @example
 * <Toolbar
 *   theme="dark"
 *   export={{ label: "Export", disabled: false, onClick: handleExport }}
 *   search={{ placeholder: "Search...", value: searchValue, onChange: setSearchValue }}
 *   filter={{ onClick: openFilter, activeCount: 2 }}
 *   manageColumns={{ onClick: openManageColumns }}
 *   actions={[{ label: "Add", onClick: handleAdd }]}
 * />
 */
const Toolbar = ({
  theme,
  size = 'small',
  export: exportConfig,
  search,
  filter,
  manageColumns,
  actions = [],
  content,
  subContent,
  labels,
}: ToolbarProps) => {
  const hasActiveFilters =
    filter?.state &&
    filter?.columns &&
    filter?.onChange &&
    Object.values(filter.state).some((v) => v !== null && v !== undefined);

  return (
    <Box className={styles.tableActions}>
      <Box className={styles.tableActionsRow}>
        <Box className={styles.toolbarLeftSection}>
          {search && (
            <Box className={styles.searchWrapper}>
              <Input
                name="table-search"
                type="text"
                placeholder={search.placeholder ?? labels?.searchPlaceholder ?? 'Search...'}
                theme={theme}
                size={size}
                controlled={true}
                value={search.value ?? ''}
                helperIcon={{
                  category: 'global',
                  name: search.value ? 'close' : 'search',
                }}
                handleIconClick={search.value ? () => search.onChange?.('') : undefined}
                onChange={(e) => search.onChange?.(e.target.value)}
                showValidation={false}
              />
            </Box>
          )}
          {content}
        </Box>
        <Box className={styles.toolbarRightSection}>
          {Array.isArray(actions) &&
            actions.map((action, index) => (
              <Button
                key={index}
                size={size}
                label={action.label}
                iconLeft={action.icon}
                gaComponent={`toolbar-${action.label.toLowerCase().replace(/\s+/g, '-')}`}
                dataTestId={
                  action.dataTestId || `toolbar-${action.label.toLowerCase().replace(/\s+/g, '-')}`
                }
                handleClick={action.onClick}
                theme={theme}
                type="tertiary"
                color="sapphire"
              />
            ))}
          {manageColumns && Array.isArray(actions) && actions.length > 0 && (
            <Separator orientation="vertical" theme={theme} className={styles.toolbarSeparator} />
          )}
          {manageColumns && (
            <Button
              size={size}
              label={labels?.manageColumns ?? 'Manage Columns'}
              iconLeft={{
                category: 'global',
                name: 'hamburger',
              }}
              gaComponent="table-manage-columns"
              dataTestId="table-manage-columns-button"
              handleClick={manageColumns.onClick}
              theme={theme}
              type="tertiary"
              color="sapphire"
            />
          )}
          {manageColumns && filter && (
            <Separator orientation="vertical" theme={theme} className={styles.toolbarSeparator} />
          )}
          {filter && (
            <Button
              size={size}
              label={
                filter.activeCount
                  ? (labels?.filterWithCount ?? 'Filter ({{count}})').replace(
                      '{{count}}',
                      String(filter.activeCount)
                    )
                  : (labels?.filter ?? 'Filter')
              }
              iconLeft={{
                category: 'global',
                name: 'filters',
              }}
              gaComponent="table-filter"
              dataTestId="table-filter-button"
              handleClick={filter.onClick}
              theme={theme}
              type="tertiary"
              color="sapphire"
            />
          )}
          {(manageColumns || filter) && exportConfig && (
            <Separator orientation="vertical" theme={theme} className={styles.toolbarSeparator} />
          )}
          {exportConfig && (
            <Button
              size={size}
              label={exportConfig.label}
              iconLeft={{
                category: 'global',
                name: 'download',
              }}
              gaComponent="table-export"
              dataTestId="table-export-button"
              handleClick={exportConfig.onClick}
              disabled={exportConfig.disabled}
              theme={theme}
              type="tertiary"
              color="sapphire"
            />
          )}
        </Box>
      </Box>
      {hasActiveFilters && filter && (
        <FilterPills
          filterState={filter.state!}
          columns={filter.columns!}
          onFilterChange={filter.onChange!}
          theme={theme}
          dataTestId="toolbar-filter-pills"
          labels={{
            clearAll: labels?.clearAll,
          }}
        />
      )}
      {subContent && <Box className={styles.toolbarSubContent}>{subContent}</Box>}
    </Box>
  );
};

export default Toolbar;
